import React, { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { resendConfirmationEmailThunk } from '../redux/userSlice'
import { AppDispatch } from '../store'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Mail, RefreshCw } from 'lucide-react'
import { useToast } from '../components/ui/use-toast'

const useAppDispatch = () => useDispatch<AppDispatch>()

const ConfirmationSent: React.FC = () => {
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const dispatch = useAppDispatch()
  const { toast } = useToast()
  const [isResending, setIsResending] = useState(false)

  const handleResend = async () => {
    if (!email) return

    setIsResending(true)
    try {
      await dispatch(resendConfirmationEmailThunk(email)).unwrap()
      toast({
        title: 'Email Sent',
        description: 'A new confirmation email has been sent to your inbox.',
        variant: 'default',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error || 'Failed to resend confirmation email.',
        variant: 'destructive',
      })
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center py-8 relative">
      <div className="fixed inset-0 -z-10 bg-[#F4F7EF]"></div>

      <div className="w-full max-w-md px-4">
        <Card className="rounded-[28px] border-0 bg-[#FFFFFF] py-4 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
          <CardHeader className="text-center pb-0">
            <div className="flex justify-center mb-4">
              <div className="bg-[#C0FD72]/10 p-3 rounded-full">
                <Mail className="w-10 h-10 text-[#3F7A0A]" />
              </div>
            </div>
            <CardTitle className="text-3xl font-black text-[#0F172A] tracking-tight">
              Check your email
            </CardTitle>
            <CardDescription className="mt-2 text-[#4B5563]">
              We&apos;ve sent a confirmation link to{email ? ' ' : ''}
              {email && <strong className="text-[#0F172A]">{email}</strong>}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4 text-center">
              <p className="text-[#374151] text-sm">
                Please check your inbox and click the link to verify your account and continue.
              </p>
              <p className="text-[#374151] text-sm">
                If you don't see the email within a minute or two, please check your spam folder.
              </p>

              <div className="bg-[#F9FAFB] p-4 rounded-lg border border-[#E5E7EB] text-xs text-[#4B5563]">
                <p className="mb-2">
                  Did not receive the email? Check your spam folder or try resending.
                </p>
                {email && (
                  <Button
                    onClick={handleResend}
                    variant="outline"
                    size="sm"
                    disabled={isResending}
                    className="mt-2 bg-transparent border-[#E5E7EB] text-[#0F172A] hover:bg-[#F3F4F6] hover:text-[#0F172A]"
                  >
                    {isResending ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Resend Confirmation Email'
                    )}
                  </Button>
                )}
              </div>

              <div className="pt-4 border-t border-[#E5E7EB]">
                <Link
                  to={
                    searchParams.get('returnTo')
                      ? `/signin?returnTo=${encodeURIComponent(searchParams.get('returnTo')!)}`
                      : '/signin'
                  }
                  className="text-[#3F7A0A] hover:text-[#3F7A0A] font-medium text-sm"
                >
                  Return to Sign In
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ConfirmationSent
